import { DigestSettingsForm } from "store/types/news";
import { useContextSelector } from "use-context-selector";

import { StoreContext } from "..";
import { IStore } from "../store-context";
import {
  FileRepoFilters,
  IAccount,
  IAudiences,
  IBookmark,
  IBookmarkType,
  IDefaultAccountBookmarks,
  IRole,
  IUser,
  Nullable,
  PipelineViewConfig,
  UserLight
} from "../types";

export const useUserSelector = (): IUser => useContextSelector(StoreContext, (store: IStore) => store.userModule.user);

export const useUserIdSelector = (): number | undefined =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.user?.id);

export const useUserBlankTemplateIdSelector = (): number =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.user?.blank_template_id);

export const useRolesSelector = (): IRole[] =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.roles);

export const useAudiencesSelector = (): IAudiences =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.audiences);

export const useUserRoleSelector = (): IRole | null =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.user.role);

export const useAccountIdSelector = (): number =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.user.account_id);

export const useAccountSelector = (): IAccount | undefined =>
  useContextSelector(StoreContext, (store: IStore) => store?.userModule?.account);

export const usePipelineViewConfigSelector = (): PipelineViewConfig | undefined =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.pipelineViewConfig);

export const useTokenSelector = (): string =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.token);

export const useBroadcastTokenSelector = (): string =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.broadcast_token);

export const useReportsSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.account.is_report_enabled);

export const useBenchmarkSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.account.show_benchmark);

export const usePlaidSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.account.show_plaid);

export const useGPTSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.account.allow_gpt);

export const useSelectBookmarksAll = (): IBookmark[] =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.bookmarks);

export const useSelectBookmarksByType = (type: IBookmarkType): IBookmark[] =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.bookmarks.filter((item) => item.type === type));

export const useUserPasswordReminderSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.user.password_reminder);

export const useUserPasswordExpiredSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.user.password_expired);

export const useDateFormatSelect = (): string =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.account.account_date_format || "MM/DD/YYYY");

export const useManagersSelect = (): UserLight[] =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.managers);

export const useShowBatchIdSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.account.show_batch_id);

export const useFileScanningSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.account.sanitize_documents);

export const useHideCompletionPopupSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => Boolean(store.userModule.hideCompletionPopup));

export const useHideExtractorGuideSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => Boolean(store.userModule.hideExtractorGuide));

export const useHideWelcomePopupSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => Boolean(store.userModule.hideWelcomePopup));

export const useWelcomeVideoUrlSelector = (): string | null =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.account.welcome_video_url);

export const useLastSeenSelector = (): string | null =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.user.last_seen);

export const useFilesFiltersSelector = (): FileRepoFilters =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.filesFilters);

export const useFileRepoColumnsSelector = (): Record<string, boolean> =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.fileRepoColumns);

export const useSelectedBookmarkSelector = (): Nullable<IBookmark["id"]> =>
  useContextSelector(StoreContext, (state: IStore) => state.userModule.selectedBookmark);

export const useOpportunityIdSelector = (): number =>
  useContextSelector(StoreContext, (state: IStore) => state.userModule.opportunity_id);

export const useAllowDealTabOpenSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.account.allow_deal_tab_open);

export const useShowUnlockFullAccessSelect = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.showUnlockFullAccess);

export const useAreOnlyExternalAccountsSelect = () =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.user.has_only_external_accounts);

export const useNumberFormatSelector = () =>
  useContextSelector(StoreContext, (state: IStore) => state.userModule.numberFormat);

export const usePickUserPermissions = (query: string) =>
  useContextSelector(
    StoreContext,
    (store: IStore) => store.userModule.user.permissions && store.userModule.user.permissions[query]
  );

export const useUserPermissions = () =>
  useContextSelector(
    StoreContext,
    (store: IStore) => store.userModule.user.permissions && store.userModule.user.permissions
  );

export const useDefaultAccountBookmarksSelector = (): IDefaultAccountBookmarks =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.default_bookmarks);

export const useWorkspaceColumnsSelector = (): Record<string, boolean> =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.workspaceColumns || {});

export const usePortfolioConstructSelector = (): number | null =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.portfolioConstruct);

export const useAllowLocalizationSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.account.show_localization);

export const useAmortizationTableColumnsSelector = (): Record<string, boolean> =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.amortizationTableColumns || {});

export const useDigestSettingsSelector = (): DigestSettingsForm =>
  useContextSelector(StoreContext, (store: IStore) => store.userModule.digestSettings);

export const useOnboardingStatusSelector = (): boolean =>
  useContextSelector(StoreContext, (store: IStore) => Boolean(store.userModule.onboardingCompleted));
